import { Box, Button, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInUser } from "../utilities/adminservice";
import { useDispatch } from "react-redux";
import { loaderOff, loaderOn, login, openSnackbar } from "../redux/actions";
import { useSignInForm } from "../custom_forms/SignInForm";
import { emailRegex } from "../CustomRegex/CustomRegex";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import store from "../redux/store";
import { fermerSession, lireJeton } from "../api/role";

const ACCENT = "#1F1E1B";

export default function Signin() {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const { formValues, setFormValues } = useSignInForm();
  const [showPassword, setShowPassword] = useState(false);

  /* ── Auth logic ── */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "password") {
      // À la connexion, seul le serveur juge le mot de passe : la règle de
      // complexité bloquait des mots de passe valides (#, ., -, _…) (Z1-23).
      setFormValues({ ...formValues, [name]: { ...formValues[name], value, error: value === "" } });
    } else if (name === "email") {
      setFormValues({ ...formValues, [name]: { ...formValues[name], value, error: !emailRegex.test(value) } });
    } else {
      setFormValues({ ...formValues, [name]: { ...formValues[name], value, error: value === "" } });
    }
  };

  const validateForm = () => {
    let invalidSubmit = false;
    let newFormValues = { ...formValues };
    for (const field of Object.keys(formValues)) {
      if (formValues[field].value === "") {
        newFormValues = { ...newFormValues, [field]: { ...newFormValues[field], error: true } };
      }
    }
    setFormValues(newFormValues);
    Object.values(newFormValues).every((f: any) => {
      if (f.error) { invalidSubmit = f.error; dispatch(loaderOff()); return false; }
      return true;
    });
    dispatch(loaderOff());
    return invalidSubmit;
  };

  /* Jeton illisible : pas de session, jamais de rôle par défaut. Le rôle est
     relu dans le jeton à chaque besoin (api/role.ts), il n'est plus stocké. */
  const handleLogin = (token: any): boolean => {
    if (!lireJeton(typeof token === "string" ? token : null)) {
      fermerSession();
      return false;
    }
    localStorage.removeItem("adminRole");
    sessionStorage.setItem("jwtToken", token);
    sessionStorage.setItem("isLoggedIn", "true");
    store.dispatch(login(token));
    return true;
  };

  const handleSignIn = async () => {
    if (!validateForm()) {
      const formData = new FormData();
      formData.append("email", formValues.email.value);
      formData.append("password", formValues.password.value);
      dispatch(loaderOn());
      try {
        const res: any = await signInUser(formData);
        if (res != "Invalid Username and Password" && res != null) {
          dispatch(loaderOff());
          if (handleLogin(res)) {
            navigate("/accueil");
          } else {
            dispatch(openSnackbar("Session refusée : jeton illisible.", "red"));
          }
        } else {
          dispatch(loaderOff());
          dispatch(openSnackbar("Identifiants invalides. Veuillez réessayer.", "red"));
        }
      } catch {
        dispatch(loaderOff());
      }
    }
  };

  /* ── Shared styles ── */
  const inputSx = {
    "& .MuiInputBase-root": {
      background: "rgba(31,30,27,0.04)",
      border: "1px solid rgba(31,30,27,0.08)",
      borderRadius: "8px",
      color: "#1F1E1B",
      fontSize: "14px",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": { borderColor: "rgba(31,30,27,0.14)" },
      "&.Mui-focused": {
        borderColor: "rgba(31,30,27,0.4)",
        background: "rgba(31,30,27,0.03)",
      },
    },
    "& .MuiFilledInput-underline::before, & .MuiFilledInput-underline::after": { display: "none" },
    "& input": { padding: "13px 15px", color: "#1F1E1B" },
    "& input::placeholder": { color: "#57534A", opacity: 1 },
    "& .MuiFormHelperText-root": { color: "#A23A0D", mt: 0.5, fontSize: "11px" },
    "& .MuiInputAdornment-root": { mr: 1 },
  };

  const labelSx = {
    display: "block",
    fontSize: "10px",
    color: "#57534A",
    textTransform: "uppercase" as const,
    letterSpacing: "1.5px",
    mb: "6px",
    fontFamily: "inherit",
  };

  return (
    <Box sx={{
      height: "100vh",
      background: "var(--c-bg)",
      display: "flex",
      flexDirection: "row",
      overflow: "hidden",
    }}>

      {/* ── Left column ── */}
      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        p: "60px",
        "@media (max-width:768px)": { display: "none" },
      }}>
        {/* Logo officiel, tel quel : la charte claire n'impose plus de le recolorer. */}
        <img src="/assets/static/svg/applogo.svg" alt="Tribe" style={{ width: 230, height: "auto" }} />

        {/* Tagline */}
        <Typography sx={{
          mt: "28px",
          fontSize: "13px",
          color: "#57534A",
          letterSpacing: "4px",
          textTransform: "uppercase",
          textAlign: "center",
          fontFamily: "inherit",
        }}>
          Le terrain parle. Nous l'écoutons.
        </Typography>

        {/* Vertical divider */}
        <Box sx={{
          position: "absolute",
          right: 0,
          top: "10%",
          height: "80%",
          width: "1px",
          background: "rgba(31,30,27,0.25)",
        }} />
      </Box>

      {/* ── Right column ── */}
      <Box sx={{
        width: { xs: "100%", sm: "420px" },
        background: "var(--c-surface)",
        borderLeft: "1.5px solid #1F1E1B",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: { xs: "24px", sm: "44px" },
        py: "48px",
        "@media (max-width:768px)": { alignItems: "center" },
      }}>
        <Box sx={{ width: "100%", maxWidth: 360, mx: "auto" }}>

          {/* Eyebrow */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2.5 }}>
            <Box sx={{ width: 20, height: 1, background: ACCENT, flexShrink: 0 }} />
            <Typography sx={{
              fontSize: "10px",
              color: "#57534A",
              textTransform: "uppercase",
              letterSpacing: "3px",
              fontFamily: "inherit",
            }}>
              Back-office
            </Typography>
          </Box>

          {/* Title */}
          <Typography sx={{
            fontSize: "28px",
            fontWeight: 700,
            color: "#1F1E1B",
            letterSpacing: "-0.5px",
            lineHeight: 1.1,
            mb: "6px",
            fontFamily: "var(--font-display)",
          }}>
            Connexion
          </Typography>

          {/* Subtitle */}
          <Typography sx={{
            fontSize: "13px",
            color: "#57534A",
            mb: "32px",
            fontFamily: "inherit",
          }}>
            Espace réservé aux administrateurs
          </Typography>

          {/* Email */}
          <Box sx={{ mb: "20px" }}>
            <Typography sx={labelSx}>E-mail</Typography>
            <TextField
              fullWidth variant="filled" name="email" type="email" autoComplete="off"
              onChange={handleChange}
              value={formValues.email.value}
              error={formValues.email.error}
              helperText={formValues.email.error && formValues.email.errorMessage}
              InputProps={{ placeholder: "votre@email.com", disableUnderline: true }}
              sx={inputSx}
            />
          </Box>

          {/* Password */}
          <Box>
            <Typography sx={labelSx}>Mot de passe</Typography>
            <TextField
              fullWidth variant="filled" name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="off"
              onChange={handleChange}
              value={formValues.password.value}
              error={formValues.password.error}
              helperText={formValues.password.error && formValues.password.errorMessage}
              InputProps={{
                placeholder: "••••••••",
                disableUnderline: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{ color: "#57534A", "&:hover": { color: "#1F1E1B" } }}
                      size="small"
                    >
                      {showPassword
                        ? <Visibility sx={{ fontSize: 18 }} />
                        : <VisibilityOff sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          {/* Forgot password */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", my: "4px", mb: "24px" }}>
            <Link to="/forgotpassword" style={{ textDecoration: "none" }}>
              <Typography sx={{
                fontSize: "11px",
                color: "#57534A",
                "&:hover": { color: ACCENT },
                transition: "color 0.15s ease",
                fontFamily: "inherit",
              }}>
                Mot de passe oublié ?
              </Typography>
            </Link>
          </Box>

          {/* Submit */}
          <Button
            fullWidth
            onClick={handleSignIn}
            sx={{
              background: ACCENT,
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              padding: "12px",
              borderRadius: "6px",
              border: "1.5px solid #1F1E1B",
              textTransform: "none",
              fontFamily: "inherit",
              transition: "background 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                background: "#1F1E1B",
                boxShadow: "none",
              },
            }}
          >
            Se connecter
          </Button>

          {/* Footer */}
          <Typography sx={{
            mt: "24px",
            fontSize: "11px",
            color: "#57534A",
            textAlign: "center",
            fontFamily: "inherit",
          }}>
            Tribe · Plateforme terrain Afrique
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
